import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./customersList.css";
import {
  FiSearch,
  FiPlus,
  FiFilter,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiChevronDown,
  FiChevronRight,
  FiShoppingCart,
  FiPhone,
} from "react-icons/fi";
import { BiExport } from "react-icons/bi";
import CustomerForm from "./customerForm";

const PAGE_SIZE = 10;
const API_BASE_URL = process.env.REACT_APP_EP || "http://localhost:5000";

const formatDate = (isoString) => {
  if (!isoString) return "-";
  const date = new Date(isoString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// ✅ Format currency
const formatINR = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);
};

const emptyCustomer = {
  id: null,
  name: "",
  email: "",
  phone: "",
  state: "",
  district: "",
  taluk: "",
  panchayat: "",
  landmark: "",
  pincode: "",
  note: "",
  dairy: "",
  status: "active",
  avatarUrl: "",
  createdAt: null,
  orders: undefined,
  balance: undefined,
};

// Legacy customer mapping (kept for add/edit compatibility)
const mapApiCustomerToClient = (c) => ({
  id: c.customer_id,
  name: c.customer_name,
  email: c.customer_email,
  phone: c.phone_number,
  state: c.state || "",
  district: c.district || "",
  taluk: c.taluk || "",
  panchayat: c.panchayat || "",
  landmark: c.landmark || "",
  pincode: c.pincode || "",
  note: c.customer_note || "",
  dairy: c.dairy || "", 
  status: c.status || "active",
  avatarUrl: c.image_url || "",
  createdAt: c.created_at,
  orders: c.total_orders != null ? Number(c.total_orders) : undefined,
  balance: c.balance != null ? Number(c.balance) : undefined,
});

// ✅ UPDATED: Map API panchayat -> frontend shape (includes phone_number and status)
const mapApiPanchayatToClient = (p) => ({
  id: p.id,
  name: p.name || "",
  email: p.email || "",
  phone: p.phoneNumber || p.phone_number || "",
  state: p.state || "Karnataka",
  district: p.district || "",
  taluk: p.taluk || "",
  panchayat: p.name || "",
  landmark: p.address || "",
  pincode: p.pincode || "",
  locationUrl: p.locationUrl || p.location_url || "",
  note: p.note || "", 
  dairy: p.dairy || "", 
  status: p.status || "active",
  avatarUrl: p.image_url || "",
  imageUrl: p.imageUrl || p.image_url || "",
  createdAt: p.created_at || null,
  orderCount: p.orderCount != null ? Number(p.orderCount) : 0,
  totalOrderAmount: p.totalOrderAmount != null ? Number(p.totalOrderAmount) : 0,
  customAmount: p.customAmount != null ? Number(p.customAmount) : 0,           // custom field
  grandTotal: p.grandTotal != null ? Number(p.grandTotal) : 0,                  // grand total field
  lastOrderDate: p.lastOrderDate || null,
  pendingOrders: p.pendingOrders != null ? Number(p.pendingOrders) : 0,
  deliveredOrders: p.deliveredOrders != null ? Number(p.deliveredOrders) : 0,
  balance: p.balance != null ? Number(p.balance) : 0,
});

const CustomersList = () => {
  const [mode, setMode] = useState("table");
  const [customers, setCustomers] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [districtFilter, setDistrictFilter] = useState("all");
  const [talukFilter, setTalukFilter] = useState("all");

  // Filter options from API
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);

  // Pagination totals from backend
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ✅ NEW: Summary stats
  const [summaryStats, setSummaryStats] = useState(null);

  const navigate = useNavigate();

  // -------- Fetch districts --------
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/locations/districts`);
        const data = await res.json();
        if (data.success) {
          setDistricts(data.data || []);
        }
      } catch (err) {
        console.error("Error fetching districts:", err);
      }
    };

    fetchDistricts();
  }, []);

  // -------- Fetch taluks when district changes --------
  useEffect(() => {
    const fetchTaluks = async (districtName) => {
      if (districtName === "all") {
        setTaluks([]);
        return;
      }
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/locations/taluks?district=${encodeURIComponent(
            districtName
          )}`
        );
        const data = await res.json();
        if (data.success) {
          setTaluks(data.data || []);
        }
      } catch (err) {
        console.error("Error fetching taluks:", err);
      }
    };

    setTalukFilter("all");
    fetchTaluks(districtFilter);
  }, [districtFilter]);

  // -------- Reset page when filters/search change --------
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, districtFilter, talukFilter]);

  // ✅ NEW: Fetch summary stats
  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const params = new URLSearchParams();
        if (districtFilter !== "all") {
          params.append("district", districtFilter);
        }
        if (talukFilter !== "all") {
          params.append("taluk", talukFilter);
        }

        const res = await fetch(
          `${API_BASE_URL}/api/panchayaths/stats/summary?${params}`
        );
        const data = await res.json();

        if (data.success) {
          setSummaryStats(data.data);
        }
      } catch (err) {
        console.error("Error fetching summary:", err);
      }
    };

    fetchSummary();
  }, [districtFilter, talukFilter]);

  // -------- Fetch panchayaths with order stats --------
  useEffect(() => {
    const fetchPanchayaths = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: currentPage,
          limit: PAGE_SIZE,
        });

        if (searchTerm.trim()) {
          params.append("search", searchTerm.trim());
        }
        if (districtFilter !== "all") {
          params.append("district", districtFilter);
        }
        if (talukFilter !== "all") {
          params.append("taluk", talukFilter);
        }

        const token = sessionStorage.getItem("authToken");
        const headers = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetch(
          `${API_BASE_URL}/api/panchayaths/fetch?${params}`,
          { headers }
        );

        let data = null;
        try {
          data = await res.json();
        } catch {
          // non-JSON
        }

        console.log("GET /panchayaths/fetch:", res.status, data);

        if (!res.ok || !data || data.success === false) {
          const msg =
            res.status >= 500 || !data
              ? "Unable to load records. Please try again."
              : data.message || data.error || "Failed to fetch records.";
          setError(msg);
          setCustomers([]);
          setTotalItems(0);
          setTotalPages(1);
          return;
        }

        const list = Array.isArray(data?.data?.panchayaths)
          ? data.data.panchayaths
          : [];

        const pagination = data.data.pagination || {};
        const mapped = list.map(mapApiPanchayatToClient);

        setCustomers(mapped);
        setTotalItems(pagination.total ?? mapped.length);
        setTotalPages(pagination.totalPages ?? 1);
      } catch (err) {
        console.error("Error fetching panchayaths:", err);
        setError(
          "Unable to load records. Please check your connection and try again."
        );
        setCustomers([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchPanchayaths();
  }, [currentPage, searchTerm, districtFilter, talukFilter]);

  const handleViewCustomer = (customer) => {
    navigate(`/customers/${customer.id}`);
  };

  // ✅ NEW: Navigate to create order for this panchayat
  const handleCreateOrder = (customer) => {
    sessionStorage.setItem(
      "selectedPanchayat",
      JSON.stringify({
        id: customer.id,
        name: customer.name,
        taluk: customer.taluk,
        district: customer.district,
      })
    );
    sessionStorage.setItem("orderAction", "salesOrder");
    navigate("/orders");
  };

  const truncateEmail = (email, length = 10) => {
    if (!email) return "";
    return email.length > length ? email.slice(0, length) + "..." : email;
  };

  // ✅ NEW: Format phone number for display
  const formatPhone = (phone) => {
    if (!phone) return "-";
    // Format as XXX-XXX-XXXX or show as is
    if (phone.length === 10) {
      return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
    }
    return phone;
  };

  // ✅ NEW: Get status display class
  const getStatusClass = (status) => {
    const s = (status || "active").toLowerCase();
    if (s === "active") return "status-active";
    if (s === "inactive") return "status-inactive";
    if (s === "blocked") return "status-blocked";
    return "status-active";
  };

  const districtOptions = useMemo(
    () => [
      { value: "all", label: "All Districts" },
      ...districts.map((d) => ({ value: d.name, label: d.name })),
    ],
    [districts]
  );

  const talukOptions = useMemo(
    () => [
      { value: "all", label: "All Taluks" },
      ...taluks.map((t) => ({ value: t.name, label: t.name })),
    ],
    [taluks]
  );

  const hasActiveFilters = districtFilter !== "all" || talukFilter !== "all";

  const activeFilterCount = [
    districtFilter !== "all",
    talukFilter !== "all",
  ].filter(Boolean).length;

  const changePage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleAddCustomerClick = () => {
    setEditingCustomer(null);
    setMode("form");
  };

  const handleEditCustomer = (customer) => {
  setEditingCustomer({
    ...customer,
    note: customer?.note ?? "",
    dairy: customer?.dairy ?? "",   // ✅ ensure present
  });
  setMode("form");
};

  const handleCancelForm = () => {
    setEditingCustomer(null);
    setMode("table");
  };

  const handleSaveCustomer = (saved) => {
    let customerObj = null;

    if (saved) {
      if (saved.customer_id) {
        customerObj = mapApiCustomerToClient(saved);
      } else if (saved.id && saved.name) {
        customerObj = mapApiPanchayatToClient(saved);
      } else {
        customerObj = saved;
      }
    }

    if (!customerObj || !customerObj.id) {
      setEditingCustomer(null);
      setMode("table");
      return;
    }

    setCustomers((prev) => {
      const idx = prev.findIndex((c) => c.id === customerObj.id);
      if (idx !== -1) {
        const copy = [...prev];
        copy[idx] = { ...prev[idx], ...customerObj };
        return copy;
      }
      return [customerObj, ...prev];
    });

    setCurrentPage(1);
    setEditingCustomer(null);
    setMode("table");
  };

  const handleDeleteCustomer = async (id) => {
  if (!window.confirm("Are you sure you want to delete this panchayat? This action cannot be undone."))
    return;

  try {
    setDeletingId(id);
    setError(null);

    const token = sessionStorage.getItem("authToken");
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    // ✅ FIXED: Changed from /api/customers/delete to /api/panchayaths/delete
    const res = await fetch(`${API_BASE_URL}/api/panchayaths/delete/${id}`, {
      method: "DELETE",
      headers,
    });

    let data = null;
    try {
      data = await res.json();
    } catch {
      // ignore json error
    }

    if (!res.ok || (data && data.success === false)) {
      let msg =
        res.status >= 500 || !data
          ? "Unable to delete panchayat. Please try again."
          : data.message || data.error || "Failed to delete panchayat.";
      setError(msg);
      alert(msg);
      return;
    }

    setCustomers((prev) => prev.filter((c) => c.id !== id));
    setTotalItems((prev) => Math.max(0, prev - 1));
    
    // Show success message
    alert("Panchayat deleted successfully!");
  } catch (err) {
    console.error("Error deleting panchayat:", err);
    const msg =
      "Unable to delete panchayat. Please check your connection and try again.";
    setError(msg);
    alert(msg);
  } finally {
    setDeletingId(null);
  }
};

  const handleClearFilters = () => {
    setDistrictFilter("all");
    setTalukFilter("all");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  // ---------- FORM MODE ----------
  if (mode === "form") {
    return (
      <CustomerForm
        initialData={editingCustomer || emptyCustomer}
        onCancel={handleCancelForm}
        onSave={handleSaveCustomer}
      />
    );
  }

  // ---------- TABLE MODE ----------
  return (
    <div className="customers-page">
      {/* Error toast */}
      {error && (
        <div className="error-toast">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* HEADER */}
      <div className="customers-header">
        <div className="customers-header-top">
          <div className="customers-header-left">
            <div className="customers-header-main">
              <h1 className="customers-title">Panchayats</h1>

              <div className="customers-header-right">
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={() => console.log("Export customers")}
                >
                  <BiExport className="btn-icon" />
                  <span>Export</span>
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleAddCustomerClick}
                >
                  <FiPlus className="btn-icon" />
                  <span>Add Panchayat</span>
                </button>
              </div>
            </div>

            <div className="customers-breadcrumbs">
              <span className="breadcrumb-link">Dashboard</span>
              <FiChevronRight className="breadcrumb-separator-icon" />
              <span className="breadcrumb-current">Panchayat List</span>
            </div>
          </div>
        </div>

        {/* ✅ Summary Stats Cards (commented as requested) */}
        {/* {summaryStats && (
          <div className="panchayat-summary-cards">
            <div className="summary-card">
              <div className="summary-card-value">{summaryStats.totalPanchayaths}</div>
              <div className="summary-card-label">Total Panchayats</div>
            </div>
            <div className="summary-card">
              <div className="summary-card-value">{summaryStats.panchayathsWithOrders}</div>
              <div className="summary-card-label">With Orders</div>
            </div>
            <div className="summary-card">
              <div className="summary-card-value">{summaryStats.totalOrders}</div>
              <div className="summary-card-label">Total Orders</div>
            </div>
            <div className="summary-card">
              <div className="summary-card-value">{formatINR(summaryStats.totalRevenue)}</div>
              <div className="summary-card-label">Total Revenue</div>
            </div>
          </div>
        )} */}

        {/* Search / Filters Row */}
        <div className="customers-header-bottom">
          <div className="customers-search-wrapper">
            <FiSearch className="customers-search-icon" />
            <input
              type="text"
              className="customers-search-input"
              placeholder="Search panchayat, taluk, district, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="customers-filters-right">
            <button
              type="button"
              className={`filters-btn ${showFilters ? "active" : ""} ${
                hasActiveFilters ? "has-filters" : ""
              }`}
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <FiFilter className="filters-icon" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="filter-count-badge">{activeFilterCount}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* FILTER DROPDOWN PANEL */}
      {showFilters && (
        <div className="customers-filter-panel">
          <div className="filter-panel-content">
            <div className="filter-group">
              <label className="filter-label">District</label>
              <select
                className="customers-filter-select"
                value={districtFilter}
                onChange={(e) => setDistrictFilter(e.target.value)}
              >
                {districtOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Taluk</label>
              <select
                className="customers-filter-select"
                value={talukFilter}
                onChange={(e) => setTalukFilter(e.target.value)}
                disabled={districtFilter === "all"}
              >
                {talukOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-actions">
              <button
                type="button"
                className="filter-clear-btn"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters && !searchTerm.trim()}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABLE CARD */}
      <div className="customers-card">
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
          </div>
        )}

        <div className="customers-table-wrapper">
          <table className="customers-table">
            <thead>
              <tr>
                <th className="col-customer-name">
                  <div className="customer-header-cell">
                    <input type="checkbox" />
                    <div className="header-with-sort">
                      <span>Panchayat Name</span>
                      <FiChevronDown className="sort-icon" />
                    </div>
                  </div>
                </th>
                {/* ✅ CHANGED: Phone Number instead of Taluk/District */}
                <th className="col-phone">
                  <span>Phone Number</span>
                </th>
                {/* Order Count Column */}
                <th className="col-orders">
                  <div className="header-with-sort">
                    <span>Orders</span>
                    <FiChevronDown className="sort-icon" />
                  </div>
                </th>
                {/* Total Amount Column */}
                <th className="col-total-amount">
                  <div className="header-with-sort">
                    <span>Total Amount</span>
                    <FiChevronDown className="sort-icon" />
                  </div>
                </th>
                {/*  Status instead of Balance */}
                <th className="col-status">
                  <div className="header-with-sort">
                    <span>Status</span>
                    <FiChevronDown className="sort-icon" />
                  </div>
                </th>
                {/* Last Order Column */}
                <th className="col-last-order">
                  <span>Last Order</span>
                </th>
                <th className="col-action">
                  <span>Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {totalItems === 0 ? (
                <tr>
                  <td colSpan={7} className="empty-table-cell">
                    {loading
                      ? "Loading records..."
                      : error
                      ? "No records loaded due to an error."
                      : "No panchayats match your filters."}
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id}>
                    {/* Panchayat Name */}
                    <td className="col-customer-name" data-label="Panchayat">
                      <div className="customer-cell">
                        <input type="checkbox" className="row-checkbox" />
                        <div className="customer-info">
                          <div className="customer-name">
                            {customer.name || customer.panchayat}
                          </div>
                          {customer.email && (
                            <div className="customer-email">
                              {truncateEmail(customer.email, 20)}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* ✅ CHANGED: Phone Number */}
                    <td className="col-phone" data-label="Phone">
                      <div className="phone-cell">
                        {customer.phone ? (
                          <>
                            <FiPhone size={14} className="phone-icon" />
                            <span>{formatPhone(customer.phone)}</span>
                          </>
                        ) : (
                          <span className="no-phone">-</span>
                        )}
                      </div>
                    </td>

                    {/* Order Count */}
                    <td className="col-orders" data-label="Orders">
                      <div className="order-count-badge-wrapper">
                        <span
                          className={`order-count-badge ${
                            customer.orderCount > 0 ? "has-orders" : "no-orders"
                          }`}
                        >
                          {customer.orderCount}
                        </span>
                        {customer.pendingOrders > 0 && (
                          <span className="pending-badge" title="Pending orders">
                            {customer.pendingOrders} pending
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Total Order Amount */}
                    {/* <td className="col-total-amount" data-label="Total Amount">
                      <span
                        className={`amount-value ${
                          customer.totalOrderAmount > 0 ? "has-amount" : "no-amount"
                        }`}
                      >
                        {formatINR(customer.totalOrderAmount)}
                      </span>
                    </td> */}

                    {/* Total Order Amount → Grand Total */}
<td className="col-total-amount" data-label="Total Amount">
  <span
    className={`amount-value ${
      customer.grandTotal > 0 ? "has-amount" : "no-amount"
    }`}
  >
    {formatINR(customer.grandTotal)}
  </span>
</td>

                    {/*  Status */}
                    <td className="col-status" data-label="Status">
                      <span className={`status-pill ${getStatusClass(customer.status)}`}>
                        {(customer.status || "active").charAt(0).toUpperCase() + 
                         (customer.status || "active").slice(1)}
                      </span>
                    </td>

                    {/* Last Order Date */}
                    <td className="col-last-order" data-label="Last Order">
                      {customer.lastOrderDate ? (
                        <span className="last-order-date">
                          {formatDate(customer.lastOrderDate)}
                        </span>
                      ) : (
                        <span className="no-order-text">No orders</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="col-action" data-label="Action">
                      <div className="action-buttons-customer">
                        <button
                          type="button"
                          className="icon-button small"
                          title="View"
                          onClick={() => handleViewCustomer(customer)}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          className="icon-button small"
                          title="Edit"
                          onClick={() => handleEditCustomer(customer)}
                        >
                          <FiEdit2 />
                        </button>
                        {/* Create Order Button */}
                        {/* <button
                          type="button"
                          className="icon-button small primary"
                          title="Create Order"
                          onClick={() => handleCreateOrder(customer)}
                        >
                          <FiShoppingCart />
                        </button> */}
                        <button
                          type="button"
                          className="icon-button small danger"
                          title="Delete"
                          onClick={() => handleDeleteCustomer(customer.id)}
                          disabled={deletingId === customer.id}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {totalItems > 0 && (
          <div className="customers-pagination">
            <div className="pagination-info">
              {`Showing ${startItem}-${endItem} of ${totalItems}`}
            </div>
            <div className="pagination-controls">
              <button
                type="button"
                className="page-button"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    type="button"
                    className={`page-button ${page === currentPage ? "active" : ""}`}
                    onClick={() => changePage(page)}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                type="button"
                className="page-button"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersList;